import axios from 'axios';

export const REFERRAL_REALTIME_UPDATES_FEATURE = 'referral_realtime_updates';
export const REFERRAL_ENTITY_LINKING_FEATURE = 'referral_entity_linking';
export const REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE = 'referral_reply_department_filter';
export const CREATE_TASK_CHART_CLARITY_FEATURE = 'create_task_chart_clarity';
const SEND_TO_CHART_TOOLTIP = 'Files this document in the patient\'s chart';
const CREATE_TASK_TOOLTIP = 'Creates an EMR task \u2014 does not file the document in the chart';
// Gates the silent background inbox refresh after record actions (archive,
// notify, send-to-chart). Off = legacy behavior: full page reloads and
// foreground refetches. Per-org so orgs with slow inbox queries can be opted
// in for testing (or back out) independently.
export const INBOX_BACKGROUND_REFRESH_FEATURE = 'inbox_background_refresh';
// Gates SMS template variable substitution ({{first_name}}, {{referring_provider}})
// in the send-SMS modal and the greeting-suppression rule. Off = legacy behavior:
// the raw template message is always prefixed with "Hello[ FirstName], ".
export const SMS_TEMPLATE_VARIABLES_FEATURE = 'sms_template_variables';
// Gates the redesigned Inbox (InboxRedesign). Off = legacy ReceivedRecords, which
// renders no search/filter/label controls — which is why the guided
// "Filters, search and labels" tutorial is only offered when this is on.
export const INBOX_REDESIGN_FEATURE = 'inbox_redesign';
// Gates the clearer inbox action wording: "Assign Document" becomes "Add assignee"
// on every surface that names the action (record-details button, referral action
// bar, assign modal, guided tutorial, help guide). Off = today's wording, exactly.
export const INBOX_ACTION_CLARITY_FEATURE = 'inbox_action_clarity';
// Whether archiving a record also closes its open assignments. NOT readable from
// the flags cache: the `archive_on_assign` Feature row is `active: true` as soon as
// an organization is subscribed, whatever the subscription's own
// `settings: { enabled: ... }` says — so a subscribed-but-disabled organization
// reads ON from `/api/v1/features` while archiving still leaves assignments open.
// The setting is published by its own endpoint instead.
export const ARCHIVE_ON_ASSIGN_SETTINGS_PATH = '/api/v1/archive_on_assign_settings';

/**
 * Fetches feature flags from GET - api/v1/features
 */
export const fetchFeatureFlags = () => axios.get('/api/v1/features');

// In-flight request memo, shared by every cache-miss path in this module.
//
// Why: one record-viewer open makes ~22 `verifyFeatureAccess` calls across the
// rendered tree (147 call sites in 59 files repo-wide), and on a cold cache each
// one used to issue its own identical `GET /api/v1/features`.
//
// What it memoizes: the NETWORK REQUEST only, never the payload. Callers still
// re-read localStorage on every call and only join the memo when the cache cannot
// answer. That matters, because three paths rewrite the cache mid-session --
// `validateAuth` (on every full page load), recapture activation, and
// `loadFeatureSettings` -- and a memoized payload would serve a stale answer past
// any of them. A request-level memo cannot: the very next caller reads the fresh
// cache and never reaches the network.
//
// Lifetime: released in a `finally` when the request settles, on rejection as well
// as success, so a failed fetch can never leave a permanently-stuck promise. It is
// deliberately NOT negative-cached -- a burst against a failing server costs one
// request instead of ~22, the next burst costs one more, and no retry loop is
// introduced. It is also released whenever the cache is authoritatively rewritten
// (`saveFeatureFlags`) or dropped (`clearFeatureFlags`, which `changeDefaultOrganization`
// calls on an org switch), so an answer computed for the previous cache generation
// is never handed to a caller that arrives after it.
//
// `fetchFeatureFlags` itself is intentionally left un-deduped: its callers in
// `actions/auth.js` and `RecaptureSales` fetch across session boundaries (login,
// OTP, password reset, activation), where reusing a request issued under the
// previous session would be wrong.
let inFlightFeatureFlagsRequest = null;

const releaseInFlightFeatureFlagsRequest = () => {
  inFlightFeatureFlagsRequest = null;
};

const requestFeatureFlags = () => {
  if (inFlightFeatureFlagsRequest) return inFlightFeatureFlagsRequest;

  const pending = fetchFeatureFlags().finally(() => {
    // Identity guard: `saveFeatureFlags`/`clearFeatureFlags` may already have
    // released this slot (and a newer request may own it) before we settle.
    if (inFlightFeatureFlagsRequest === pending) releaseInFlightFeatureFlagsRequest();
  });

  inFlightFeatureFlagsRequest = pending;
  return pending;
};

/**
 * Saves feature flags in local storage
 */
export const saveFeatureFlags = (flags) => {
  releaseInFlightFeatureFlagsRequest();
  try {
    const serializedData = JSON.stringify(flags);
    localStorage.setItem('flags', serializedData);
  } catch {
    return [];
  }
};

export const clearFeatureFlags = () => {
  releaseInFlightFeatureFlagsRequest();
  try {
    localStorage.removeItem('flags');
  } catch {
    return false;
  }
  return true;
};

/**
 * Saves user flags in local storage
 */
export const saveUserFlags = (userFlags) => {
  try {
    const serializedData = JSON.stringify(userFlags);
    localStorage.setItem('user_flags', serializedData);
  } catch {
    return [];
  }
};

/**
 * Checks if local storage has the feature flag in question.
 * Handles both array format and response-shaped format { data: { flags: [...] } }
 */
export const hasFeatureInLocalStorage = (featureName) => {
  try {
    const serializedData = localStorage.getItem('flags');
    const featureFlags = JSON.parse(serializedData);
    if (featureFlags === null) {
      return false;
    }

    // Normalize to array - handle both direct array and response-shaped object
    let flagsArray;
    if (Array.isArray(featureFlags)) {
      flagsArray = featureFlags;
    } else if (Array.isArray(featureFlags?.data?.flags)) {
      flagsArray = featureFlags.data.flags;
    } else {
      return false;
    }

    const [feature] = flagsArray.filter((flag) => flag.name === featureName);
    return feature?.active || false;
  } catch {
    return false;
  }
};

const normalizeFeaturePayload = (featureFlags) => {
  if (Array.isArray(featureFlags)) {
    return { flags: featureFlags };
  }
  if (Array.isArray(featureFlags?.data?.flags)) {
    return featureFlags.data;
  }
  if (Array.isArray(featureFlags?.flags)) {
    return featureFlags;
  }

  return null;
};

/**
 * Whether the localStorage flags cache holds a usable answer RIGHT NOW.
 *
 * `hasFeatureInLocalStorage` collapses two very different states into `false`:
 * "the cache says this flag is off" and "there is no readable cache". A caller that
 * has to render something before an async check resolves needs to tell them apart —
 * with a warm cache a `false` is authoritative and it can render the off-branch on
 * the first paint; with a cold one it must wait for the network.
 *
 * Returns false when storage is blocked or the payload does not parse, which is the
 * correct answer for both: no synchronous answer is available.
 */
export const featureFlagCacheIsWarm = () => {
  try {
    return normalizeFeaturePayload(JSON.parse(localStorage.getItem('flags'))) !== null;
  } catch {
    return false;
  }
};

export const chartClarityTooltips = (autoUploadEmr) => {
  if (!hasFeatureInLocalStorage(CREATE_TASK_CHART_CLARITY_FEATURE)) {
    return { sendToChart: undefined, createTask: undefined };
  }

  return {
    sendToChart: SEND_TO_CHART_TOOLTIP,
    createTask: autoUploadEmr === 'Kareo' ? undefined : CREATE_TASK_TOOLTIP,
  };
};

// One place for every user-visible string the assign action is named by, so the
// button, the overflow menu item, the modal title, the guided tutorial and the
// help guide can never drift apart mid-rollout.
//
// Read at RENDER time, never at module load: the flags cache is written at login
// and rewritten on an org switch, both of which can happen after this module is
// first imported. Every helper below is therefore a function, not a constant.
const ASSIGN_ACTION_WORDING = {
  legacy: {
    action: 'Assign Document',
    modalTitle: 'Assign Document Within Medsender',
    // Today's tooltips on the redesigned row, kept verbatim. "Reassign" overstates
    // what the endpoint does (see `assignActionLabel`) but it is what ships today,
    // and switch-off has to be today's copy byte for byte.
    assignTooltip: 'Assign',
    reassignTooltip: 'Reassign',
  },
  clarity: {
    action: 'Add assignee',
    modalTitle: 'Add assignee',
    // Both tooltips deliberately identical: the action is additive whether or not
    // someone is already assigned, so there is nothing truthful to distinguish.
    assignTooltip: 'Add assignee',
    reassignTooltip: 'Add assignee',
  },
};

const assignActionWording = () => (
  hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE)
    ? ASSIGN_ACTION_WORDING.clarity
    : ASSIGN_ACTION_WORDING.legacy
);

/**
 * Whether the clearer assign wording is on. Only for callers that need to know the
 * wording changed rather than what it says — the help guide drops a screenshot that
 * pictures the old label. Prefer the wording helpers below for anything user-facing.
 */
export const assignWordingIsClear = () => hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE);

/**
 * Label for the assign button and the inbox overflow-menu item.
 *
 * One label, whether or not the record already has an assignee. There is no
 * change-an-assignee wording because there is no change-an-assignee action:
 * `POST /api/v1/assigned_documents` builds a new `AssignedDocument` and never
 * completes the open one, so a record with an assignee ends up with two open
 * assignments on two people's lists. A button promising to replace the current
 * assignee would be a name that does not match its behaviour — the exact defect
 * ENG-3339 exists to remove. `assignActionTooltip` keeps an `assigned` argument
 * only because today's tooltips split on it and switch-off must not move.
 */
export const assignActionLabel = () => assignActionWording().action;

/**
 * Title of the assign-record modal. The Mark Complete and Reassign entry points pass
 * `markComplete`: that flow closes the current assignment on submit and opens a new
 * one for someone else, so it keeps today's title rather than the add-an-assignee
 * wording, which describes neither half of what it does.
 */
export const assignModalTitle = ({ markComplete = false } = {}) => (
  markComplete
    ? ASSIGN_ACTION_WORDING.legacy.modalTitle
    : assignActionWording().modalTitle
);

/**
 * Tooltip on the record-details assignee control. `assigned` exists for the switch-off
 * variant only, whose two tooltips read "Assign" and "Reassign" today. The clarity
 * variant answers "Add assignee" either way — see `assignActionLabel`.
 */
export const assignActionTooltip = (assigned) => (
  assigned ? assignActionWording().reassignTooltip : assignActionWording().assignTooltip
);

// True only of a plain assignment, and only while archiving does NOT close
// assignments. Neither condition is knowable here: whether the modal was opened to
// mark the current assignment complete belongs to the modal, and the archive setting
// comes off its own endpoint.
const ASSIGNMENT_STAYS_OPEN_NOTE = 'The record stays in this inbox. The assignee closes the assignment themselves — archiving the record does not close it.';

/**
 * The line explaining that archiving does not close an assignment, or null when the
 * wording switch is off. Says nothing about WHEN the sentence is true — see
 * `fetchArchiveClosesAssignments` and the modal's mark-complete gate.
 */
export const assignmentStaysOpenNote = () => (
  hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE) ? ASSIGNMENT_STAYS_OPEN_NOTE : null
);

/**
 * Whether archiving a record also closes its open assignments, per the organization's
 * `archive_on_assign` setting.
 *
 * Tri-state, because the answer gates a categorical claim about archive behavior and
 * "we do not know" is a state callers have to be able to see:
 *
 *  - `true` — archiving closes assignments.
 *  - `false` — archiving leaves them open. An answer, not a fallback: an explicit
 *    `enabled: false`, a 200 that publishes no setting, or a 404. The 404 is the
 *    deliberate compatibility path: a server without the route cannot have the
 *    setting either, so the setting is off for every organization it serves and
 *    callers should say so. Kept now that the route ships alongside this code,
 *    because a bundle outliving the deploy that removed the route is the same
 *    situation.
 *  - `null` — unknown. A 5xx, a timeout, a dropped connection, a 401: the request
 *    came back with nothing about the setting, so there is no answer to report.
 *
 * Collapsing `null` into `false` is the bug this shape exists to prevent: it let the
 * assign modal state, for as long as the server kept failing, that archiving does not
 * close an assignment — to organizations where it does.
 */
export const fetchArchiveClosesAssignments = async () => {
  try {
    const response = await axios.get(ARCHIVE_ON_ASSIGN_SETTINGS_PATH);
    return response?.data?.settings?.enabled === true;
  } catch (error) {
    // 404 is the one error that answers the question: no such endpoint means no
    // organization has the setting turned on. Every other rejection — including a
    // network error, which carries no `response` at all — leaves it unknown.
    if (error?.response?.status === 404) return false;
    return null;
  }
};

/**
 * Loads feature flags from feature flags
 * Fetches from backend if local storage is empty
 */
export const loadFeatureFlagPayload = async () => {
  try {
    const serializedData = localStorage.getItem('flags');
    const featureFlags = JSON.parse(serializedData);
    const payload = normalizeFeaturePayload(featureFlags);
    if (!payload) {
      throw new Error('Flags is not an array or array is empty');
    }
    return payload;
  } catch {
    const featureFlags = await requestFeatureFlags();
    saveFeatureFlags(featureFlags);
    return featureFlags.data;
  }
};

export const loadFeatureFlags = async () => {
  const payload = await loadFeatureFlagPayload();
  return payload.flags || [];
};

export const loadFeatureSettings = async (featureName) => {
  let payload = await loadFeatureFlagPayload();
  if (!payload.feature_settings) {
    const featureFlags = await requestFeatureFlags();
    saveFeatureFlags(featureFlags);
    payload = featureFlags.data;
  }
  return payload.feature_settings?.[featureName] || {};
};

/**
 * Takes in the feature name and verifies,
 * if user has access to this feature.
 * @param {String} feature_name
 */
export const verifyFeatureAccess = async (featureName) => {
  const featureFlags = await loadFeatureFlags();
  const [feature] = featureFlags.filter((flag) => flag.name === featureName);
  if (feature && feature.active) return feature.active;
  return false;
};

/**
 * Verifies if user has access to a feature.
 * @param {String} featureName
 */
export const verifyReferralMgmtAccess = async () => {
  const referralManagementStatus = localStorage.getItem('m8_referral_management');
  return referralManagementStatus === 'true';
};

export const verifyAnalyticsAccess = async () => {
  const analyticsAccess = localStorage.getItem('m8_view_referral_analytics');
  return analyticsAccess === 'true';
};

export const verifyReceivedInboxAccess = async () => {
  const receivedInboxAccess = localStorage.getItem('m8_access_received_inbox');
  return receivedInboxAccess === 'true';
};

export const verifySentInboxAccess = async () => {
  const sentInboxAccess = localStorage.getItem('m8_access_sent_inbox');
  return sentInboxAccess === 'true';
};

export const verifyVoiceInboxAccess = async () => {
  const voiceInboxAccess = localStorage.getItem('m8_access_voice_inbox');
  return voiceInboxAccess === 'true';
};

export const verifyPatientRecaptureAccess = async () => {
  const patientRecaptureAccess = localStorage.getItem('m8_patient_recapture');
  return patientRecaptureAccess === 'true';
};

// Patient Engagement access is role-based — the key is backend-derived from
// User#patient_engagement (feature flag + admin/role check).
export const verifyPatientEngagementAccess = async () => {
  const patientEngagementAccess = localStorage.getItem('m8_patient_engagement');
  return patientEngagementAccess === 'true';
};

export const verifyReferralManagementAccess = async () => {
  const referralManagementAccess = localStorage.getItem('m8_referral_management');
  return referralManagementAccess === 'true';
};
